/*
  Reverse Edge Mapping Utilities

  This module handles conversion from document edges back to API format,
  including workflow outcome options transformation.
*/

import type { ApiEdge, Edge as DocEdge } from '../../types';

/**
 * Key mappings for workflow outcome options (reverse direction)
 * Maps from frontend snake_case back to backend camelCase
 */
const REVERSE_WORKFLOW_OUTCOME_KEY_MAPPINGS = {
  opens_alert: 'opensAlert',
  investigation_type: 'investigationType',
  investigation_error: 'investigationError',
  opens_investigation: 'opensInvestigation',
  opens_application: 'opensApplication',
} as const;

/**
 * Transform workflow outcome options from frontend format back to backend format
 *
 * Frontend stores these keys under config.workflow_outcome_options, backend expects
 * them directly under config in camelCase.
 *
 * @param config - The edge config object (will be mutated)
 */
function transformWorkflowOutcomeOptionsToApi(config: Record<string, any>): void {
  if (!config.workflow_outcome_options) return;

  const workflowOutcomeOptions = config.workflow_outcome_options as Record<string, unknown>;

  // Map new snake_case keys back to old camelCase keys for backend compatibility
  Object.entries(REVERSE_WORKFLOW_OUTCOME_KEY_MAPPINGS).forEach(([snakeKey, camelKey]) => {
    if (workflowOutcomeOptions[snakeKey] !== undefined) {
      if (snakeKey === 'investigation_type' && typeof workflowOutcomeOptions[snakeKey] === 'object' && workflowOutcomeOptions[snakeKey] !== null) {
        // Extract only the name from investigation type object for backend compatibility
        const investigationTypeObj = workflowOutcomeOptions[snakeKey] as { name?: string };
        if (investigationTypeObj.name !== undefined) {
          config[camelKey] = investigationTypeObj.name;
        }
      } else {
        config[camelKey] = workflowOutcomeOptions[snakeKey];
      }
    }
  });

  // Remove the nested workflow_outcome_options object since backend expects flat structure
  delete config.workflow_outcome_options;
}

/**
 * Converts a document model edge to save-ready API format
 *
 * This function handles:
 * - workflow_outcome_options transformation back to backend format
 * - Condition normalization (null -> empty string)
 * - Optional precedence inclusion
 *
 * @param edge - The document edge to convert
 * @returns The API edge ready for backend saving
 */
export function mapDocEdgeToSaveApiEdge(edge: DocEdge): ApiEdge {
  const config = { ...(edge.config || {}) };

  // Handle workflow_outcome_options transformation for backend compatibility
  transformWorkflowOutcomeOptionsToApi(config);

  const result: ApiEdge = {
    source: edge.from.node,
    target: edge.to.node,
    condition: edge.condition || '', // Backend expects empty string, not null
    config,
  };

  // TODO: We do not support precedence in the backend yet.
  // Only include precedence if it's defined
  // if (edge.precedence !== undefined) {
  //   result.precedence = edge.precedence;
  // }

  return result;
}

